// Сериализация состояния карты в query-параметры (round-trip идемпотентный).

import { DEFAULT_FRONT_UNIT, type FrontUnit } from './maps/frontsMath'
import { writeOverlaysUrl, parseOverlaysUrl } from './context/contextUrl'
import { WAVE_STEP_DEFAULT } from './map/waveContours'
import { childOfIsotherm, parseIsothermSpread } from './map/isothermFamily'
import type { FleetSegment } from './models/fleetSegments'

export interface MapState {
  layer: string
  depth: number
  date: string
  species: number | null
  // ── Блок «Наложения» (общий для обоих разделов, пишет активная страница) ──
  overlays: string[]
  /** Сегменты флота (Свой/РФ/Ин.флот) — живые позиции судов на карте. */
  fleet: FleetSegment[]
  /** Оверлей «Волнение» (изо-линии + окраска стрелок по высоте волны). */
  waves: boolean
  /** Шаг изолиний волнения, м (дефолт 0.5). */
  waveStep: number
  /** «Показать суда без категорий» при выбранном виде (NPFC-фильтр). */
  showUncat: boolean
  /** Выбор флагов «Ин.флота»; пусто = все флаги. */
  flags: string[]
  lat: number | null
  lng: number | null
  zoom: number | null
  /** Шаг изобат, м; null = раздел «Изобаты» выключен. */
  isobath: number | null
  /** Градус активной изотермы (°C); null = изотерма выключена. */
  isotherm: number | null
  /** Градус ПОВЕРХНОСТНОЙ изотермы (на 0 м); null = выключена. Дочерняя к
   *  `isotherm`: без основной не живёт. */
  isothermSurface: number | null
  /** Шаг смежных изотерм (°C); null = смежные выключены. Тоже дочерний. */
  isothermSpread: number | null
  /** Порог силы фронтов в выбранной единице (>0); null = фронты выключены. */
  fronts: number | null
  /** Единица порога фронтов: '100km' (обычные) или '10km' (резкие). */
  frontsUnit: FrontUnit
}

interface CatalogLike {
  id: string
  has_depth: boolean
  overlay: boolean
}

export const DEFAULT_MAP_STATE: MapState = {
  layer: 'sst',
  depth: 0,
  date: '',
  species: null,
  overlays: [],
  fleet: [],
  waves: false,
  waveStep: WAVE_STEP_DEFAULT,
  flags: [],
  isobath: null,
  showUncat: false,
  lat: null,
  lng: null,
  zoom: null,
  isotherm: null,
  isothermSurface: null,
  isothermSpread: null,
  fronts: null,
  frontsUnit: DEFAULT_FRONT_UNIT,
}

export function parseMapState(params: URLSearchParams): MapState {
  const num = (v: string | null): number | null => (v == null || v === '' ? null : Number(v))
  const ov = parseOverlaysUrl(params)
  const isotherm = num(params.get('iso'))
  return {
    layer: params.get('layer') || DEFAULT_MAP_STATE.layer,
    depth: num(params.get('depth')) ?? 0,
    date: params.get('date') || '',
    species: num(params.get('species')),
    overlays: ov.overlays,
    fleet: ov.fleet,
    waves: ov.waves,
    waveStep: ov.waveStep,
    showUncat: ov.showUncat,
    flags: ov.flags,
    lat: num(params.get('lat')),
    lng: num(params.get('lng')),
    zoom: num(params.get('zoom')),
    isobath: num(params.get('isobath')),
    isotherm,
    // Дочерние: без основной изотермы их тумблеров на экране нет, значит и
    // линий быть не должно (childOfIsotherm).
    isothermSurface: childOfIsotherm(isotherm, num(params.get('isosurf'))),
    isothermSpread: childOfIsotherm(isotherm, parseIsothermSpread(num(params.get('isostep')))),
    // Сила фронта строго > 0 (0/отрицательное = выключено, не «нулевой фронт»).
    fronts: (() => {
      const f = num(params.get('front'))
      return f != null && f > 0 ? f : null
    })(),
    frontsUnit: params.get('frontu') === '10km' ? '10km' : '100km',
  }
}

export function mapStateToQuery(state: MapState): string {
  const p = new URLSearchParams()
  p.set('layer', state.layer)
  p.set('depth', String(state.depth))
  if (state.date) p.set('date', state.date)
  if (state.species != null) p.set('species', String(state.species))
  // Наложения (overlays/fleet/waves/showuncat) — общий сериализатор с «Моделями».
  writeOverlaysUrl(p, {
    overlays: state.overlays,
    fleet: state.fleet,
    waves: state.waves,
    showUncat: state.showUncat,
    waveStep: state.waveStep,
    flags: state.flags,
  })
  if (state.lat != null) p.set('lat', String(state.lat))
  if (state.lng != null) p.set('lng', String(state.lng))
  if (state.zoom != null) p.set('zoom', String(state.zoom))
  // != null, а не truthy: изотерма 0 °C — валидный градус, не «выключено».
  if (state.isobath != null) p.set('isobath', String(state.isobath))
  if (state.isotherm != null) {
    p.set('iso', String(state.isotherm))
    // Дочерние пишем ТОЛЬКО при живой основной — и пишем всегда, когда они
    // включены: дефолт ползунка шага (1 °C) не совпадает с «выключено», и
    // молчание отдало бы по ссылке одиночную изотерму вместо семейства.
    if (state.isothermSurface != null) p.set('isosurf', String(state.isothermSurface))
    if (state.isothermSpread != null) p.set('isostep', String(state.isothermSpread))
  }
  // Фронты, наоборот: только > 0 (сила — это перепад, ноль бессмыслен).
  if (state.fronts != null && state.fronts > 0) {
    p.set('front', String(state.fronts))
    // Единицу пишем только для активных фронтов и только не-дефолтную (10 км).
    if (state.frontsUnit === '10km') p.set('frontu', '10km')
  }
  return p.toString()
}

/** Глубина недоступна для оверлеев и слоёв без has_depth (или неизвестных). */
export function depthDisabled(layer: string, catalog: CatalogLike[]): boolean {
  const spec = catalog.find(c => c.id === layer)
  if (!spec) return true
  return spec.overlay || !spec.has_depth
}
